import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Filter, Loader2, Trash2, MessageSquarePlus } from 'lucide-react';
import { api } from '../utils/api';
import { sessionKeyManager } from '../utils/encryption';
import AnalysisModal from '../components/AnalysisModal';
import FollowUpModal from '../components/FollowUpModal';
import { format } from 'date-fns';
import { getCategoryBadgeStyle } from '../utils/categoryColors';

const History = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpDownload, setFollowUpDownload] = useState(null);
  const [decryptedTexts, setDecryptedTexts] = useState({});
  // Track which download is queued for confirm-deletion via the AlertDialog.
  // Using an in-app dialog instead of window.confirm() — some installed PWA
  // shells (iOS standalone, older Android WebViews) silently dismiss the
  // native confirm() call, which is why users report "Delete does nothing".
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDownloads();
  }, [selectedCategory, selectedTheme]);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedCategory !== 'all') filters.category = selectedCategory;
      if (selectedTheme !== 'all') filters.theme = selectedTheme;

      const data = await api.getDownloads(filters);
      setDownloads(data);

      // Decrypt texts
      await decryptDownloads(data);
    } catch (error) {
      console.error('Failed to fetch downloads:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const decryptDownloads = async (downloadsData) => {
    try {
      const key = await sessionKeyManager.getOrCreateKey();
      const { encryption } = await import('../utils/encryption');

      const decrypted = {};
      for (const download of downloadsData) {
        try {
          const text = await encryption.decrypt(
            download.encrypted_text,
            download.encryption_iv,
            key
          );
          decrypted[download.id] = text;
        } catch (err) {
          console.error(`Failed to decrypt download ${download.id}:`, err);
          decrypted[download.id] = null;
        }
      }
      setDecryptedTexts(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
    }
  };

  const handleDownloadClick = (download) => {
    setSelectedDownload({
      ...download,
      decryptedText: decryptedTexts[download.id] || '',
    });
    setShowAnalysisModal(true);
  };

  const handleDelete = (downloadId) => {
    // Open the in-app confirm dialog (window.confirm is unreliable in
    // installed PWA contexts — see pendingDeleteId comment above).
    setPendingDeleteId(downloadId);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId || deleting) return;
    const idToDelete = pendingDeleteId;
    setDeleting(true);
    try {
      await api.deleteDownload(idToDelete);
      setDownloads((prev) => prev.filter((d) => d.id !== idToDelete));
      // Also purge any decrypted-text cache for this entry so it disappears cleanly.
      setDecryptedTexts((prev) => {
        const next = { ...prev };
        delete next[idToDelete];
        return next;
      });
      toast.success('Download deleted');
      setPendingDeleteId(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error?.message || 'Failed to delete download');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddFollowUp = (download, e) => {
    e.stopPropagation();
    setFollowUpDownload(download);
    setShowFollowUpModal(true);
  };

  const handleFollowUpAdded = async (text, reflectionType) => {
    try {
      const updated = await api.addFollowUp(
        followUpDownload.id,
        text,
        reflectionType
      );

      // If the Analysis modal is currently open on this same entry, refresh its
      // selectedDownload state so the new follow-up appears in the timeline
      // immediately — no need for the user to close/reopen.
      if (selectedDownload && selectedDownload.id === followUpDownload.id) {
        setSelectedDownload((prev) => ({
          ...prev,
          ...updated,
          // Preserve the already-decrypted plaintext we computed earlier
          decryptedText: prev.decryptedText,
        }));
      }

      // Refresh the list so the updated_at / preview reflect the new follow-up
      await fetchDownloads();
    } catch (error) {
      throw error;
    }
  };

  const categoryColors = {}; // legacy — replaced by shared util

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-semibold mb-3 text-[var(--sd-fg)]">Download History</h1>
          <p className="text-base text-[var(--sd-muted-fg)]">
            Review your spiritual journey and insights
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Filter size={16} className="text-[var(--sd-primary)]" />
                <span>Filter by:</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                  data-testid="history-filter-category-select"
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Family">Family</SelectItem>
                    <SelectItem value="Personal Growth">Personal Growth</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={selectedTheme}
                  onValueChange={setSelectedTheme}
                  data-testid="history-filter-theme-select"
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Themes</SelectItem>
                    <SelectItem value="Peace">Peace</SelectItem>
                    <SelectItem value="Purpose">Purpose</SelectItem>
                    <SelectItem value="Guidance">Guidance</SelectItem>
                    <SelectItem value="Faith">Faith</SelectItem>
                    <SelectItem value="Wisdom">Wisdom</SelectItem>
                    <SelectItem value="Trust">Trust</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(selectedCategory !== 'all' || selectedTheme !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedTheme('all');
                  }}
                  className="text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
                >
                  Clear
                </Button>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Downloads list */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center py-20"
            >
              <Loader2 size={32} className="animate-spin text-[var(--sd-primary)]" />
            </motion.div>
          ) : downloads.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Card className="glass-card p-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-[var(--sd-muted-fg)]" />
                <h3 className="text-xl font-semibold mb-2 text-[var(--sd-fg)]">No downloads yet</h3>
                <p className="text-[var(--sd-muted-fg)] mb-6">
                  Start recording your spiritual insights to see them here.
                </p>
                <Button
                  onClick={() => (window.location.href = '/record')}
                  className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)]"
                >
                  Record Your First Download
                </Button>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {downloads.map((download, idx) => (
                <motion.div
                  key={download.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card
                    className="glass-card p-5 cursor-pointer hover:border-[var(--sd-primary)] transition-colors"
                    onClick={() => handleDownloadClick(download)}
                    data-testid={`history-entry-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Text preview */}
                        {decryptedTexts[download.id] ? (
                          <p className="text-base mb-3 line-clamp-2 text-[var(--sd-fg)] font-medium">
                            {decryptedTexts[download.id]}
                          </p>
                        ) : (
                          <p className="text-base mb-3 line-clamp-2 text-[var(--sd-muted-fg)] italic">
                            Tap to open this download
                          </p>
                        )}

                        {/* Metadata */}
                        <div className="flex flex-wrap items-center gap-3">
                          {download.analysis && (
                            <>
                              <Badge
                                style={getCategoryBadgeStyle(download.analysis.category)}
                                className="border font-semibold"
                              >
                                {download.analysis.category}
                              </Badge>
                              {download.analysis.themes.slice(0, 3).map((theme, i) => (
                                <Badge
                                  key={i}
                                  className="bg-[var(--sd-surface-hover)] text-[var(--sd-fg)] border border-[var(--sd-border)] font-medium"
                                >
                                  {theme}
                                </Badge>
                              ))}
                            </>
                          )}
                          <span className="text-xs text-[var(--sd-muted-fg)]">
                            {format(new Date(download.created_at), 'MMM d, yyyy • h:mm a')}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleAddFollowUp(download, e);
                          }}
                          className="text-[var(--sd-primary)] hover:brightness-110"
                          data-testid={`add-followup-${idx}`}
                          title="Add follow-up reflection"
                        >
                          <MessageSquarePlus size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDelete(download.id);
                          }}
                          className="text-[var(--sd-muted-fg)] hover:text-[#C88484]"
                          data-testid={`delete-entry-${idx}`}
                          title="Delete this download"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Analysis Modal */}
      <AnimatePresence>
        {showAnalysisModal && selectedDownload && selectedDownload.analysis && (
          <AnalysisModal
            analysis={selectedDownload.analysis}
            onClose={() => setShowAnalysisModal(false)}
            downloadText={selectedDownload.decryptedText}
            download={selectedDownload}
            onFollowUpRequested={() => {
              setFollowUpDownload(selectedDownload);
              setShowFollowUpModal(true);
            }}
          />
        )}
      </AnimatePresence>

      {/* Follow-Up Modal */}
      <AnimatePresence>
        {showFollowUpModal && followUpDownload && (
          <FollowUpModal
            download={followUpDownload}
            onClose={() => {
              setShowFollowUpModal(false);
              setFollowUpDownload(null);
            }}
            onFollowUpAdded={handleFollowUpAdded}
          />
        )}
      </AnimatePresence>

      {/* In-app delete confirmation — replaces the unreliable window.confirm()
          which some installed PWAs silently dismiss. */}
      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
      >
        <AlertDialogContent
          className="glass-card"
          data-testid="history-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--sd-fg)]">
              Delete this download?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--sd-muted-fg)]">
              This will permanently remove this reflection, its AI analysis,
              and any follow-ups. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="history-delete-cancel"
              className="border-[var(--sd-border)] text-[var(--sd-fg)]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="history-delete-confirm"
              className="bg-[#DC6F6F] hover:bg-[#E08A8A] text-[#1C1C1C] font-semibold"
            >
              {deleting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-2" />
                  Yes, delete it
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default History;